import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import ChartWrapper from './ChartWrapper'
import Patient_16 from './Patient_16'
import Patient_16_At from './Patient_16_at'
import Patient_32 from './Patient_32'
import Patient_32_At from './Patient_32_at'
import { selectBedInfoState, demoModeState } from '../plugins/recoil';
import { useRecoilState } from 'recoil'


const PatientWrapper = forwardRef(({bedInfo, bedData, updateKey, patientCnt, isFirst, isLeft, isHalf, handleChangeDisplay, handleMeasurements, handleTrend, handleAlarmReview, handleBedSetting, handleInitWardStatusChangeFlag, wardStatusChangeFlag,handleOpenOneBed,handleStandByConfirm,handlePrint
}, ref) => {
    const wrapperRef = useRef(null)
    const [arr, setArr] = useState([])
    const [heightChangeValue, setHeightChangeValue] = useState(1)
    const [redrawCnt, setRedrawCnt] = useState(0)
    const [selectBedInfo, ] = useRecoilState(selectBedInfoState)
    const [demoMode, ] = useRecoilState(demoModeState)
    const isNeedToChangeDisplay = useRef(false)

    const updateHeight = (patientIdx, flag) => {
        const newArr = [...arr]
        const minHeight = 12.5

        switch(flag) {
            case 'plus': {
                // 1. 다른 bed들의 가용 공간 계산
                let totalAvailable = 0
                const excesses = {}
                newArr.forEach((item, idx) => {
                    if (idx !== patientIdx) {
                        const excess = Math.max(0, item.height - minHeight)
                        excesses[idx] = excess
                        totalAvailable += excess
                    }
                })
                if (totalAvailable <= 0) break

                const actualExpand = Math.min(heightChangeValue, totalAvailable)

                // 2. 균등 배분 시도
                const otherCount = newArr.length - 1
                const equalShare = actualExpand / otherCount
                const adjustments = {}
                let totalTaken = 0

                newArr.forEach((item, idx) => {
                    if (idx !== patientIdx) {
                        if (excesses[idx] >= equalShare) {
                            adjustments[idx] = equalShare
                        } else {
                            adjustments[idx] = excesses[idx]
                        }
                        totalTaken += adjustments[idx]
                    }
                })

                // 3. 부족분이 있으면 가장 큰 bed부터 추가 확보
                let remaining = actualExpand - totalTaken
                if (remaining > 0.001) {
                    const otherIndices = Object.keys(excesses)
                        .map(Number)
                        .sort((a, b) => newArr[b].height - newArr[a].height)

                    for (const idx of otherIndices) {
                        if (remaining <= 0.001) break
                        const additionalCanGive = Math.max(0, excesses[idx] - adjustments[idx])
                        const take = Math.min(remaining, additionalCanGive)
                        adjustments[idx] += take
                        remaining -= take
                    }
                }

                // 4. 적용
                const finalTaken = Object.values(adjustments).reduce((sum, v) => sum + v, 0)
                newArr.forEach((item, idx) => {
                    if (idx !== patientIdx && adjustments[idx] > 0) {
                        const newHeight = item.height - adjustments[idx]
                        const newRenderType = getRenderType(newHeight)
                        if (newRenderType !== item.viewType) isNeedToChangeDisplay.current = true
                        newArr[idx] = { height: newHeight, viewType: newRenderType }
                    }
                })

                const newSelectedHeight = newArr[patientIdx].height + finalTaken
                const newRenderType = getRenderType(newSelectedHeight)
                if (newRenderType !== newArr[patientIdx].viewType) isNeedToChangeDisplay.current = true
                newArr[patientIdx] = { height: newSelectedHeight, viewType: newRenderType }
                break
            }
            case 'minus': {
                // 1. 선택 bed의 축소 가능량
                const canShrink = Math.max(0, newArr[patientIdx].height - minHeight)
                if (canShrink <= 0) break

                const actualShrink = Math.min(heightChangeValue, canShrink)

                // 2. 선택 bed 축소
                const newHeight = newArr[patientIdx].height - actualShrink
                const newRenderType = getRenderType(newHeight)
                if (newRenderType !== newArr[patientIdx].viewType) isNeedToChangeDisplay.current = true
                newArr[patientIdx] = { height: newHeight, viewType: newRenderType }

                // 3. 해제된 공간을 다른 bed들에게 균등 배분
                const share = actualShrink / (newArr.length - 1)
                newArr.forEach((item, idx) => {
                    if (idx !== patientIdx) {
                        const otherNewHeight = item.height + share
                        const otherNewRenderType = getRenderType(otherNewHeight)
                        if (otherNewRenderType !== item.viewType) isNeedToChangeDisplay.current = true
                        newArr[idx] = { height: otherNewHeight, viewType: otherNewRenderType }
                    }
                })
                break
            }
        }

        setArr(newArr)
    }

    const getRenderType = (patientHeight) => {
        const parsed = Math.round(patientHeight * 100) / 100
        if ((Math.round(100 / 2 * 100) / 100) <= parsed) return 4
        if ((Math.round(100 / 3 * 100) / 100) <= parsed && (Math.round(100 / 2 * 100) / 100) > parsed) return 6
        if ((Math.round(100 / 4 * 100) / 100) <= parsed && (Math.round(100 / 3 * 100) / 100) > parsed) return 8
        if ((Math.round(100 / 5 * 100) / 100) <= parsed && (Math.round(100 / 4 * 100) / 100) > parsed) return 10
        if ((Math.round(100 / 6 * 100) / 100) <= parsed && (Math.round(100 / 5 * 100) / 100) > parsed) return 12
        if ((Math.round(100 / 7 * 100) / 100) <= parsed && (Math.round(100 / 6 * 100) / 100) > parsed) return 14
        if ((Math.round(100 / 7 * 100) / 100) <= parsed && (Math.round(100 / 8 * 100) / 100) > parsed) return 16
        else return 16
    }

    useEffect(() => {
        if (patientCnt) {
            setArr(new Array(patientCnt).fill({ height : 100 / patientCnt, viewType: patientCnt * 2}))
        } else {
            setArr([])
        }
    }, [patientCnt])

    useEffect(() => {
        if (isNeedToChangeDisplay.current) {
            handleChangeDisplay(isLeft)
            isNeedToChangeDisplay.current = false
        }
        setHeightChangeValue(100 / (arr.length - 1) / 3)
    }, [arr])

    useEffect(() => {
        if (!isHalf) {
            const heightInfo = localStorage.getItem(`${isLeft ? 'left' : 'right'}Height`)
            if (heightInfo) {
                setArr(JSON.parse(heightInfo))
            }
            localStorage.removeItem(`${isLeft ? 'left' : 'right'}Height`)
        }
        handleInitWardStatusChangeFlag(0)
    }, [isHalf])

    useEffect(() => {
        setRedrawCnt(redrawCnt + 1)
    }, [selectBedInfo])

    // useEffect(() => {
    //     console.log(bedData)
    // }, [bedData])

    useImperativeHandle(ref, () => ({
        saveHeightInfo: (type) => {
            return localStorage.setItem(`${type}Height`, JSON.stringify(arr))
        },
        setHeightInfo: (type) => {
            const heightInfo = localStorage.getItem(`${type}Height`)
            if (heightInfo) {
                setArr(JSON.parse(heightInfo))
            }
            localStorage.removeItem(`${type}Height`)
        },
        getHeightInfo: () => {
            return arr
        }
    }));

    return (
        <div ref={wrapperRef} className="relative flex flex-col flex-1" style={{ maxWidth: isHalf ? '25%' : '50%', minWidth: isHalf ? '25%' : '50%' }}>
            {
                arr.map((item, index) => {                    
                    const renderType = getRenderType(item.height)
                    if (isHalf) {
                        const HalfPatientComponent = (demoMode && isFirst && index === 0) ? Patient_32_At : Patient_32
                        return (
                            <HalfPatientComponent
                                bedInfo={bedInfo ? bedInfo[index] || { numericCodeList: [] } : { numericCodeList: [] }} 
                                bedData={bedInfo && bedInfo[index] ? bedData[bedInfo[index].serial] : {}}
                                key={`patient-item-${index}`}
                                viewType={renderType}
                                clientHeight={item.height}
                                patientCnt={patientCnt}
                                patientIdx={index}
                                updateHeight={updateHeight}
                                handleTrend={handleTrend}
                                handleAlarmReview={handleAlarmReview}
                                handleBedSetting={handleBedSetting}
                                wardStatusChangeFlag={wardStatusChangeFlag}
                                handleOpenOneBed={handleOpenOneBed}
                                handleStandByConfirm={handleStandByConfirm}
                                handleMeasurements={handleMeasurements}
                                handlePrint={handlePrint}
                                // alarms={alarms}
                                // handleSilence={handleSilence}
                                // handleUnsilence={handleUnsilence}
                                // remainingSeconds={remainingSeconds}
                                // isSilenced={isSilenced}
                            />
                        )
                    }
                    if (renderType <= 16) {
                        // console.log(bedInfo ? bedInfo[index] || { numericCodeList: [] } : { numericCodeList: [] })
                        // if (bedInfo[index]) {
                        //     console.log(bedInfo[index].serial, bedData[bedInfo[index].serial])
                        // }
                        const PatientComponent = (demoMode && isFirst && index === 0) ? Patient_16_At : Patient_16
                        return (
                            <PatientComponent
                                bedInfo={bedInfo ? bedInfo[index] || { numericCodeList: [] } : { numericCodeList: [] }} 
                                bedData={(bedInfo && bedInfo[index]) ? bedData[bedInfo[index].serial] : {}}
                                key={`patient-item-${index}`}
                                viewType={renderType}
                                clientHeight={item.height}
                                patientCnt={patientCnt}
                                patientIdx={index}
                                updateHeight={updateHeight}
                                heightChangeValue={heightChangeValue}
                                handleTrend={handleTrend}
                                handleAlarmReview={handleAlarmReview}
                                handleBedSetting={handleBedSetting}
                                wardStatusChangeFlag={wardStatusChangeFlag}
                                handleOpenOneBed={handleOpenOneBed}
                                handleStandByConfirm={handleStandByConfirm}
                                handleMeasurements={handleMeasurements}
                                handlePrint={handlePrint}       
                                LeftView={isLeft}                         
                                // alarms={alarms}
                                // handleSilence={handleSilence}
                                // handleUnsilence={handleUnsilence}
                                // remainingSeconds={remainingSeconds}
                                // isSilenced={isSilenced}
                            />
                        )
                    }
                })
            }
        </div>
    )
})

export default PatientWrapper